// Seção de Preferências da IA dentro de "Minha Conta".
//
// Cobre dois eixos independentes:
//   1. Como a IA corrige suas dissertativas/redações (strictness, threshold,
//      desconto de norma culta, celebrações).
//   2. Como a IA responde quando você conversa com ela (verbosity).
//
// As prefs valem apenas em Estudo e Simulação. Prova Agendada IGNORA — segue
// critério do professor com defaults.
//
// Nota: usamos button group nativo (não ToggleGroup do Radix) pra evitar
// incompat com Preact em compat mode — o controle ficava inerte e sem highlight
// do item selecionado. Implementação manual abaixo é totalmente reativa via
// signal de aiPrefs.

import { useState } from "preact/hooks";
import { Brain, GraduationCap, MessageCircleQuestion, Sparkles, SpellCheck } from "lucide-react";
import { Slider } from "@app/ui/components/slider";
import { friendlyError } from "@app/ui/lib/friendly-error";
import {
  aiAssistantPrefs,
  aiCorrectionPrefs,
  grammarModeLabel,
  quickStudyPrefs,
  setGrammarMode,
  setGrammarPenalty,
  setQuickStudyVerbosity,
  setStrictness,
  setThreshold,
  setVerbosity,
  strictnessDescription,
  strictnessLabel,
  verbosityDescription,
  verbosityLabel,
  type AssistantVerbosity,
  type CorrectionStrictness,
  type GrammarMode
} from "./aiPrefs";

export function AiPrefsSection() {
  const correction = aiCorrectionPrefs.value;
  const assistant = aiAssistantPrefs.value;
  const quickStudy = quickStudyPrefs.value;
  const [savingMsg, setSavingMsg] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  async function run(label: string, action: () => Promise<void>) {
    setErrMsg(null);
    setSavingMsg(`Salvando ${label}…`);
    try {
      await action();
      setSavingMsg("Salvo");
      setTimeout(() => setSavingMsg(null), 1200);
    } catch (e) {
      setSavingMsg(null);
      setErrMsg(friendlyError(e, { fallback: "Não foi possível salvar agora." }));
    }
  }

  return (
    <section class="rounded-md border border-border bg-card/60 p-5">
      <header class="mb-1 flex items-center gap-2">
        <Sparkles className="size-4 text-primary" />
        <h2 class="text-sm font-bold">Preferências da IA</h2>
      </header>
      <p class="mb-4 text-xs text-muted-foreground">
        Configurações valem apenas para Modo Estudo e Simulação. Prova Agendada
        segue o critério definido pelo professor.
      </p>

      {savingMsg && (
        <p class="mb-3 text-xs text-muted-foreground">{savingMsg}</p>
      )}
      {errMsg && (
        <p class="mb-3 text-xs text-destructive">{errMsg}</p>
      )}

      {/* ===== Corretor ===== */}
      <div class="space-y-5">
        <div>
          <div class="mb-1 flex items-center gap-2">
            <GraduationCap className="size-4 text-violet-500" />
            <h3 class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Como a IA corrige você
            </h3>
          </div>

          {/* Strictness */}
          <div class="mt-3 space-y-2">
            <label class="text-sm font-medium">Modo de correção</label>
            <SegmentedButtons<CorrectionStrictness>
              value={correction.strictness}
              options={[
                { value: "standard", label: "Padrão" },
                { value: "strict", label: "Rigoroso" }
              ]}
              onChange={(v) =>
                run("modo de correção", () => setStrictness(v))
              }
            />
            <p class="text-xs text-muted-foreground">
              {strictnessDescription(correction.strictness)}
            </p>
          </div>

          {/* Threshold */}
          <div class="mt-5 space-y-2">
            <label class="text-sm font-medium">Nota mínima para "acerto"</label>
            <SegmentedButtons<number>
              value={correction.threshold}
              options={[5, 6, 7, 8].map((n) => ({ value: n, label: String(n) }))}
              onChange={(v) => run("nota de corte", () => setThreshold(v))}
            />
            <p class="text-xs text-muted-foreground">
              Nota a partir da qual a questão conta como "acerto" no contador da
              sessão e dispara animações de celebração. Nota crua salva
              continua sendo 0–10.
            </p>
          </div>

          {/* Grammar */}
          <div class="mt-5 space-y-2">
            <label class="flex items-center gap-2 text-sm font-medium">
              <SpellCheck className="size-4 text-violet-500" />
              Desconto por norma culta (dissertativas)
            </label>
            <SegmentedButtons<GrammarMode>
              value={correction.grammarMode}
              options={[
                { value: "off", label: "Não descontar" },
                { value: "judgmental", label: "Julgamental" },
                { value: "fixed", label: "Valor fixo" }
              ]}
              onChange={(v) =>
                run("modo de desconto", () => setGrammarMode(v))
              }
            />
            <p class="text-xs text-muted-foreground">
              Não vale para redações (já têm rubrica própria). Quando ativo, a
              IA lista cada erro com o trecho, o problema e a forma correta.
            </p>

            {correction.grammarMode === "fixed" && (
              <div class="mt-3 space-y-2 rounded-md border border-border bg-background/60 p-3">
                <div class="flex items-center justify-between">
                  <label class="text-xs font-medium text-muted-foreground">
                    Pontos descontados por erro
                  </label>
                  <span class="text-sm font-bold text-violet-500">
                    −{correction.grammarPenalty.toFixed(1)}
                  </span>
                </div>
                <Slider
                  min={0.1}
                  max={1.0}
                  step={0.1}
                  value={[correction.grammarPenalty]}
                  onValueChange={(arr: number[]) => {
                    const v = Array.isArray(arr) ? arr[0] : null;
                    if (typeof v !== "number") return;
                    void run("desconto por erro", () => setGrammarPenalty(v));
                  }}
                />
              </div>
            )}
          </div>

          {/* Os toggles de celebração (dissertativa por bloco + redação)
              estão em Preferências → "Comemoração ao acertar" pra ficar junto
              do master switch das animações. */}
        </div>

        {/* ===== Assistente ===== */}
        <div class="border-t border-border pt-5">
          <div class="mb-1 flex items-center gap-2">
            <Brain className="size-4 text-violet-500" />
            <h3 class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Como a IA conversa com você
            </h3>
          </div>

          <div class="mt-3 space-y-2">
            <label class="text-sm font-medium">Nível de detalhe das respostas</label>
            <SegmentedButtons<AssistantVerbosity>
              value={assistant.verbosity}
              options={[
                { value: "concise", label: "Conciso" },
                { value: "standard", label: "Padrão" },
                { value: "detailed", label: "Detalhado" }
              ]}
              onChange={(v) =>
                run("nível de detalhe", () => setVerbosity(v))
              }
            />
            <p class="text-xs text-muted-foreground">
              {verbosityDescription(assistant.verbosity)} Vale para o Assistente
              de IA dentro de uma questão e para o "Explicação com IA" da
              revisão de provas. Você também pode trocar dentro do próprio
              chat, só na sessão.
            </p>
          </div>
        </div>

        {/* ===== Tira-dúvida (QuickStudy) — verbosidade própria, separada do
            Assistente de Estudo. Vale web + WhatsApp. ===== */}
        <div class="border-t border-border pt-5">
          <div class="mb-1 flex items-center gap-2">
            <MessageCircleQuestion className="size-4 text-violet-500" />
            <h3 class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Tira-dúvida (chat rápido e WhatsApp)
            </h3>
          </div>

          <div class="mt-3 space-y-2">
            <label class="text-sm font-medium">Nível de detalhe das respostas</label>
            <SegmentedButtons<AssistantVerbosity>
              value={quickStudy.verbosity}
              options={[
                { value: "concise", label: "Conciso" },
                { value: "standard", label: "Padrão" },
                { value: "detailed", label: "Detalhado" }
              ]}
              onChange={(v) =>
                run("nível de detalhe do tira-dúvida", () =>
                  setQuickStudyVerbosity(v)
                )
              }
            />
            <p class="text-xs text-muted-foreground">
              {verbosityDescription(quickStudy.verbosity)} Vale para o
              Tira-dúvida na plataforma <strong>e pelo WhatsApp</strong> — é
              uma preferência separada do Assistente acima, então você pode
              deixar o Tira-dúvida mais direto e o Assistente mais detalhado.
              Começa em <strong>Conciso</strong>, mas você muda quando quiser.
              No WhatsApp o visual já vem enxuto (sem formatação pesada),
              independente do nível.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Button group que destaca o ativo em violeta. Implementação manual pra
 *  evitar incompat do Radix ToggleGroup com Preact compat. */
function SegmentedButtons<T extends string | number>({
  value,
  options,
  onChange
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
}) {
  return (
    <div class="inline-flex flex-wrap items-center gap-1">
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={String(opt.value)}
            type="button"
            onClick={() => onChange(opt.value)}
            class={
              "inline-flex h-8 items-center justify-center rounded-md border px-3 text-xs font-medium transition-colors " +
              (active
                ? "border-violet-500 bg-violet-500/15 text-violet-700 dark:text-violet-300"
                : "border-border bg-background hover:bg-accent hover:text-accent-foreground")
            }
            aria-pressed={active ? "true" : "false"}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/** Re-export utilitário para outros componentes lerem labels sem
 *  importar `aiPrefs` direto. */
export { grammarModeLabel, strictnessLabel, verbosityLabel };
